// Pure helpers for the bulk "Update Rho Versions" tool. No React, no network,
// so tests/version_update.test.js imports this file directly under Node.
//
// The tool changes exactly one key in each selected repo's rho.yaml,
// spec.source.targetRevision, through the same GET/PUT /rho-configuration
// calls the per-repo config page uses. Everything else in the file is kept.

const STABLE_TAG = /^v(\d+)\.(\d+)\.(\d+)$/;

/** Rho release tags that distributors may choose from (most recent stable). */
const DISTRIBUTOR_RELEASE_CHOICES = 3;

function isStableTag(tag) {
  return STABLE_TAG.test(String(tag || ''));
}

function parseTag(tag) {
  const m = String(tag || '').match(/^v?(\d+)\.(\d+)\.(\d+)/);
  return m ? m.slice(1, 4).map(Number) : null;
}

/** Newest first; non-numeric tags sort after every numeric one, alphabetically. */
function sortReleases(tags) {
  return [...(tags || [])].filter(Boolean).sort((a, b) => {
    const av = parseTag(a);
    const bv = parseTag(b);
    if (av && bv) {
      for (let i = 0; i < 3; i++) if (av[i] !== bv[i]) return bv[i] - av[i];
      // Same numbers: the plain stable tag wins over a pre-release of it.
      const as = isStableTag(a) ? 0 : 1;
      const bs = isStableTag(b) ? 0 : 1;
      return as - bs || String(a).localeCompare(String(b));
    }
    if (av) return -1;
    if (bv) return 1;
    return String(a).localeCompare(String(b));
  });
}

function sortStableReleases(tags) {
  return sortReleases((tags || []).filter(isStableTag));
}

/**
 * Tags a user may set in bulk. Admins get every release; distributors get the
 * same shortlist the config page offers them.
 */
function selectableReleases(tags, isAdmin) {
  const stable = sortStableReleases(tags);
  if (!isAdmin) return stable.slice(0, DISTRIBUTOR_RELEASE_CHOICES);
  return sortReleases(tags);
}

function latestStable(tags) {
  return sortStableReleases(tags)[0] || null;
}

/**
 * -1 when `target` is older than `current`, 0 when equal, 1 when newer,
 * null when either side is not a numeric version.
 */
function compareVersions(target, current) {
  const t = parseTag(target);
  const c = parseTag(current);
  if (!t || !c) return null;
  for (let i = 0; i < 3; i++) if (t[i] !== c[i]) return t[i] > c[i] ? 1 : -1;
  if (String(target) === String(current)) return 0;
  // Same numbers but different tag (e.g. v3.6.0 vs v3.6.0-beta-2): a change.
  return isStableTag(target) && !isStableTag(current) ? 1 : -1;
}

/** Group names that mean 16 Bit itself (the API returns "16bit" or "16Bit"). */
function is16BitOrganization(name) {
  return String(name || '').trim().toLowerCase() === '16bit';
}

/**
 * Collapse the per-organization deployments map into the list of scopes the
 * picker offers: every 16 Bit spelling merged into one entry, others as-is.
 */
function organizationScopes(deployments) {
  const scopes = new Map();
  Object.entries(deployments || {}).forEach(([name, repos]) => {
    const key = is16BitOrganization(name) ? '16bit' : name;
    const label = is16BitOrganization(name) ? '16 Bit' : name;
    const entry = scopes.get(key) || { key, label, repos: [] };
    entry.repos.push(...(repos || []));
    scopes.set(key, entry);
  });
  return [...scopes.values()].sort((a, b) => {
    if (a.key === '16bit') return -1;
    if (b.key === '16bit') return 1;
    return a.label.localeCompare(b.label);
  });
}

/**
 * Build the rows for one scope. A row is eligible when it is not archived and
 * not already on the target; the caller decides what is ticked.
 */
function buildRows({ repos, licenses = {}, institutions = {}, archived = {}, target }) {
  return (repos || []).map((repo) => {
    const info = licenses[repo] || {};
    const current = info.target_revision || info.targetRevision || '';
    const isArchived = !!archived[repo];
    const cmp = target ? compareVersions(target, current) : null;
    const onTarget = !!target && current === target;
    let reason = null;
    if (isArchived) reason = 'archived';
    else if (onTarget) reason = 'already on target';
    return {
      repo,
      institution: institutions[repo] || '',
      current,
      archived: isArchived,
      onTarget,
      // 'upgrade' | 'downgrade' | 'change' (non-numeric on one side) | null
      direction: onTarget ? null : cmp === 1 ? 'upgrade' : cmp === -1 ? 'downgrade' : target ? 'change' : null,
      eligible: !isArchived && !onTarget,
      reason,
    };
  });
}

/**
 * Set spec.source.targetRevision on a parsed rho.yaml document in place and
 * report whether anything changed. Never touches another key.
 */
function applyTargetRevision(doc, target) {
  if (!doc || typeof doc !== 'object') throw new Error('rho.yaml is empty or not a mapping');
  if (!target || typeof target !== 'string') throw new Error('No target version');
  doc.spec = doc.spec && typeof doc.spec === 'object' ? doc.spec : {};
  doc.spec.source = doc.spec.source && typeof doc.spec.source === 'object' ? doc.spec.source : {};
  const previous = doc.spec.source.targetRevision;
  if (previous === target) return { changed: false, previous };
  doc.spec.source.targetRevision = target;
  return { changed: true, previous: previous === undefined ? null : previous };
}

module.exports = {
  DISTRIBUTOR_RELEASE_CHOICES,
  isStableTag,
  sortReleases,
  sortStableReleases,
  selectableReleases,
  latestStable,
  compareVersions,
  is16BitOrganization,
  organizationScopes,
  buildRows,
  applyTargetRevision,
};
